import React, { useReducer, useRef, useState } from 'react';

import {
  Box,
  Button,
  Container,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';

import { object, ref, string, ValidationError } from 'yup';

import ImageInput from './ImageInput';
import PasswordField from '../../../../Components/PasswordField';
import { TypoSerif } from '../../../../Components/Typo';

import { useToastContext } from '../../../../Context/ToastContext';

import FireBase from '../../../../lib/fireBaseProfessional';

import { uploadFile, UPLOAD_FOLDERS } from '../../../../Services/S3';

import { useFormContext } from './FormContext';

const PersonalInformation = () => {
  const { showToastMessage } = useToastContext();
  const { updateInputs, inputs, nextStep, uploadedUrls, registrationState } = useFormContext();

  const [isNextBtnDisabled, setIsNextBtnDisabled] = useState(false);

  const [errors, updateErrors] = useReducer(errorReducer, {
    firstName: null,
    lastName: null,
    address: null,
    province: null,
    postalCode: null,
    district: null,
    password: null,
    confirmPassword: null,
    about: null,
  });

  const avatar = useRef();
  const nicImage = useRef();
  const addressProof = useRef();

  function handleChange(event) {
    const { value, name } = event.target;
    updateInputs({ [name]: value });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setIsNextBtnDisabled(true);
    updateErrors({ type: ERROR_ACTION.CLEAR });
    try {
      const validatedData = await schema.validate(inputs, {
        abortEarly: false,
      });
      updateInputs(validatedData);

      const avatarFile = avatar.current?.files?.item(0) ?? null;
      const nicImageFile = nicImage.current?.files?.item(0) ?? null;
      const addressProofFile = addressProof.current?.files?.item(0) ?? null;

      // Create the Firebase user now (the password is captured on this step).
      // The DB professional/service rows are still created later, at Confirmation.
      const fb = await FireBase.createProfessional(inputs.email, inputs.password);
      if (fb.errors) {
        showToastMessage(
          fb.errors === 'auth/email-already-in-use'
            ? 'This email is already registered. Please go back and use a different email.'
            : 'Could not create your account. Please try again.',
          'error',
        );
        return undefined;
      }
      updateInputs({ fbUid: fb.data.uid });
      registrationState.fbUserCreated = true;

      // Upload this step's images (avatar + KYC docs) to S3 now, on Next, and keep
      // the URLs. Must run after the Firebase user exists: createUploadUrl requires
      // an authenticated actor and scopes the object key by the Firebase UID.
      // Only overwrite a stored URL when a NEW file was picked — so returning to
      // this step (e.g. via "Edit Details" after a failure) without re-selecting
      // keeps the previously-uploaded images instead of clearing them.
      try {
        const [avatarUrl, nicImageUrl, addressProofUrl] = await Promise.all([
          avatarFile ? uploadFile(avatarFile, UPLOAD_FOLDERS.PROFESSIONAL_AVATAR) : null,
          nicImageFile ? uploadFile(nicImageFile, UPLOAD_FOLDERS.PROFESSIONAL_NIC) : null,
          addressProofFile
            ? uploadFile(addressProofFile, UPLOAD_FOLDERS.PROFESSIONAL_ADDRESS_PROOF)
            : null,
        ]);
        if (avatarUrl) uploadedUrls.avatar = avatarUrl;
        if (nicImageUrl) uploadedUrls.nicImage = nicImageUrl;
        if (addressProofUrl) uploadedUrls.addressProof = addressProofUrl;
      } catch (uploadError) {
        console.error('[registration] personal image upload to S3 failed', uploadError);
        showToastMessage(
          'Image upload failed. Please check your connection and try again.',
          'error',
        );
        return undefined;
      }

      nextStep();
    } catch (error) {
      if (error instanceof ValidationError) {
        const errors = error.inner.reduce(
          (errors, err) => ({ ...errors, [err.path]: err.message }),
          {}
        );
        updateErrors({ type: ERROR_ACTION.SET, payload: errors });
        return undefined;
      }

    } finally {
      setIsNextBtnDisabled(false);
    }
  }

  return (
    <Container maxWidth='sm' sx={{ my: 8 }}>
      <TypoSerif textAlign='center' variant='h4' mb={2}>
        Personal Details
      </TypoSerif>
      <Stack component='form' alignItems='center' onSubmit={handleSubmit}>
        <ImageInput tooltip='Profile Picture' ref={avatar} />

        <Stack direction='row' gap={3} mt={2}>
          <ImageInput tooltip='NIC Image' ref={nicImage} />
          <ImageInput tooltip='Address Proof' ref={addressProof} />
        </Stack>

        <Box display='grid' gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2,1fr)' }} gap={2} mt={2}>
          <TextField
            autoComplete='given-name'
            inputProps={{ spellCheck: false }}
            placeholder='First name'
            name='firstName'
            value={inputs.firstName}
            onChange={handleChange}
            helperText={errors.firstName}
            error={Boolean(errors.firstName)}
          />

          <TextField
            autoComplete='family-name'
            inputProps={{ spellCheck: false }}
            placeholder='Last name'
            name='lastName'
            value={inputs.lastName}
            onChange={handleChange}
            helperText={errors.lastName}
            error={Boolean(errors.lastName)}
          />

          <TextField
            autoComplete='address'
            inputProps={{ spellCheck: false }}
            placeholder='Address'
            name='address'
            value={inputs.address}
            onChange={handleChange}
            helperText={errors.address}
            error={Boolean(errors.address)}
            sx={{ gridColumn: { sm: 'span 2' } }}
          />
          <TextField
            autoComplete='address-level1'
            inputProps={{ spellCheck: false }}
            placeholder='Province'
            name='province'
            value={inputs.province}
            onChange={handleChange}
            helperText={errors.province}
            error={Boolean(errors.province)}
          />
          <TextField
            autoComplete='postal-code'
            inputProps={{ spellCheck: false }}
            placeholder='Post Code'
            name='postalCode'
            value={inputs.postalCode}
            onChange={handleChange}
            helperText={errors.postalCode}
            error={Boolean(errors.postalCode)}
          />
          <TextField
            inputProps={{ spellCheck: false }}
            placeholder='District'
            name='district'
            value={inputs.district}
            onChange={handleChange}
            helperText={errors.district}
            error={Boolean(errors.district)}
          />
          <PasswordField
            autoComplete='new-password'
            placeholder='Password'
            name='password'
            value={inputs.password}
            onChange={handleChange}
            helperText={errors.password}
            error={Boolean(errors.password)}
          />
          <PasswordField
            autoComplete='new-password'
            placeholder='Re enter Password'
            name='confirmPassword'
            value={inputs.confirmPassword}
            onChange={handleChange}
            helperText={errors.confirmPassword}
            error={Boolean(errors.confirmPassword)}
          />

          <TextField
            InputProps={{
              endAdornment: (
                <InputAdornment position='start' sx={{ alignSelf: 'flex-end', pb: 1 }}>
                  <div>{inputs.about.length}</div>
                </InputAdornment>
              ),
            }}
            inputProps={{ spellCheck: false }}
            multiline
            rows={4}
            name='about'
            placeholder='About'
            value={inputs.about}
            onChange={handleChange}
            helperText={errors.about}
            error={Boolean(errors.about)}
            sx={{ gridColumn: { sm: 'span 2' } }}
          />
        </Box>
        <Button color='blue' sx={{ mt: 2, px: 8 }} type='submit' disabled={isNextBtnDisabled}>
          Next
        </Button>
      </Stack>
    </Container>
  );
};

const schema = object({
  firstName: string().trim().required().label('Full Name'),
  lastName: string().trim().required().label('Last Name'),
  address: string().trim().required().label('Address'),
  province: string().trim().required().label('Province'),
  postalCode: string().trim().required().label('Postal Code'),
  district: string().trim().required().label('District'),
  password: string().required().min(6).label('Password'),
  confirmPassword: string()
    .oneOf([ref('password')], 'Password do not match')
    .label(),
  about: string()
    .trim()
    .required()
    .min(50, 'Must be at least 50 characters')
    .label('About'),
});

const ERROR_ACTION = Object.freeze({
  SET: 'SET',
  CLEAR: 'CLEAR',
});

function errorReducer(state, { type, payload }) {
  switch (type) {
    case ERROR_ACTION.SET:
      return { ...state, ...payload };
    case ERROR_ACTION.CLEAR:
      return Object.keys(state).reduce(
        (errors, key) => ({ ...errors, [key]: null }),
        {}
      );
    default:
      return state;
  }
}

export default PersonalInformation;
